/**
 * It converts number into Amount with it's corresponding magnitude
 * @param {num} value: Number to convert into amount
 * @param {boolean} hideNegativeSign: If true, then hides the negative sign of amount
 * @returns {string} - formatted string of amount
 */
export function convertNumberToAmount(num, hideNegativeSign = false) {
  if (typeof num !== "number") {
    return "";
  }
  let numberValue = num;
  let mIndex = 0;
  const magnitude = ["", "K", "M", "B", "T"];
  while (Math.abs(numberValue) >= 1000 && mIndex < magnitude.length - 1) {
    mIndex++;
    numberValue = numberValue / 1000;
  }
  const absNumber = Math.abs(numberValue);
  const noToUse = hideNegativeSign ? absNumber : numberValue;
  let result: number | string = noToUse;
  if (!Number.isInteger(result)) {
    result = result.toFixed(2);
  }

  return `${result}${magnitude[mIndex]}`;
}

/**
 * It creates formatted string of currency and amount
 * @param {number} value: Number to convert into amount
 * @param {string} currency: Currency of amount
 * @returns {string} - formatted string
 */
export function formatMCapAndEVCell(value, currency = "", viewType = "values") {
  if (typeof value !== "number") {
    return "-";
  }
  if (viewType === "values") {
    const formattedAmount = convertNumberToAmount(value, true);
    if (value < 0) {
      return `(${currency}${formattedAmount})`;
    }
    return `${currency}${formattedAmount}`;
  }
  const formattedAmount = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Math.abs(value));
  if (value < 0) {
    return `(${formattedAmount}%)`;
  }
  return `${formattedAmount}%`;
}

/**
 * Converts array of tickers to string depending on startIndex and batchsize
 * @param {number} startIndex - starting index from which it should take the ticker id
 * @param {number} batchSize - no. of ticker id it should take
 * @param {Array} tickers - list of ticker
 * @returns {string} - string of ticker id
 */
export function extractTickers(
  startIndex: number,
  batchSize: number,
  tickers: Array<string>,
): string {
  let tickerParams = "";
  for (let i = startIndex; i < startIndex + batchSize; i++) {
    if (i > tickers.length - 1) {
      break;
    } else {
      tickerParams = `${tickerParams}&ticker=${tickers[i]}`;
    }
  }
  return tickerParams;
}

export function formatCell(
  data,
  currency,
  unit,
  valueType: string,
  precision: number = 2,
) {
  const sliCompsData = data;
  if (sliCompsData === undefined || sliCompsData === null) {
    return "-";
  }

  if (sliCompsData === "NM") {
    return "NM";
  }

  const valueToUse = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  }).format(Math.abs(sliCompsData));

  let unitType = unit;
  if (
    !currency ||
    currency === "Base" ||
    valueType === "yoy" ||
    valueType === "revision"
  ) {
    currency = "";
  }
  if (unitType === "%") {
    unitType = valueType === "yoy" || valueType === "revision" ? "bps" : "%";
  } else {
    unitType = valueType === "yoy" || valueType === "revision" ? "%" : "";
  }
  if (sliCompsData < 0) {
    return `(${currency}${valueToUse}${unitType})`;
  }
  return `${currency}${valueToUse}${unitType}`;
}

/**
 * This method returns the color hex code after taking into account the luminosity passed.
 * the range for lum is 0 - 1.
 * 0 would return the same color, 1 would return lightest.
 * @param hex
 * @param lum
 * @returns {string}
 * @constructor
 */
function colorLuminance(hex, lum) {
  // validate hex string
  hex = String(hex).replace(/[^0-9a-f]/gi, "");
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  lum = lum || 0;

  // convert to decimal and change luminosity
  let rgb = "#",
    c = null,
    i = null;
  for (i = 0; i < 3; i++) {
    c = parseInt(hex.substr(i * 2, 2), 16);
    c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
    rgb += `00${c}`.substr(c.length);
  }

  return rgb;
}

export function analysisColor(percentChange, peak, baseColor) {
  let relChange = null;
  if (parseFloat(peak) === 0) {
    relChange = 1;
  } else {
    relChange = parseFloat(percentChange) / parseFloat(peak);
  }
  relChange = 1 - Math.round(relChange * 10) / 10;
  const bgColor = colorLuminance(baseColor, relChange);
  return bgColor;
}

export function getTickerIdUrl(tickerIds) {
  let tickerParams = "";
  for (let i = 0; i < tickerIds.length; i++) {
    tickerParams = `${tickerParams}&ticker=${tickerIds[i]}`;
  }

  return tickerParams;
}

export function getTickersChunksForDataCall(tickers, maxParallelCalls = 10) {
  let pointer = 0;
  const tickerChunks = [];

  for (let i = 0; i < tickers.length; i++) {
    if (pointer >= maxParallelCalls) {
      pointer = 0;
    }
    let arr = tickerChunks[pointer];
    if (arr === undefined) {
      arr = [];
      arr.push(tickers[i]);
      tickerChunks.push(arr);
    } else {
      arr.push(tickers[i]);
      tickerChunks[pointer] = arr;
    }
    pointer++;
  }
  return tickerChunks;
}

export function getSelectedColumnPickerItemId(valueArr) {
  if (valueArr) {
    return valueArr.map((item) => item.id);
  }
  return [];
}

export function getSelectedColumnPickerItemName(valueArr) {
  if (valueArr) {
    return valueArr.map((item) => item.name);
  }
  return [];
}
